import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { checkIndexedDBAvailability, getStorageQuota, requestPersistentStorage } from '../utils/indexedDBCheck';
import logger from '../utils/logger';
import './IndexedDBGuard.css';

/**
 * IndexedDBGuard component
 * Checks IndexedDB availability and blocks the app if it's not available
 * Shows a user-friendly error message with troubleshooting steps
 */
const IndexedDBGuard = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAvailable, setIsAvailable] = useState(false);
  const [errorReason, setErrorReason] = useState('');
  const [storageInfo, setStorageInfo] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const checkAvailability = async () => {
    logger.info('Starting IndexedDB availability check');
    setIsChecking(true);

    try {
      const result = await checkIndexedDBAvailability();
      setIsAvailable(result.available);

      if (!result.available) {
        logger.error('IndexedDB not available:', result.reason);
        setErrorReason(result.reason);

        // Get storage quota information for troubleshooting
        const quota = await getStorageQuota();
        setStorageInfo(quota);
      } else {
        logger.info('IndexedDB is available and functional');

        // Try to get persistent storage for better reliability
        await requestPersistentStorage();
      }
    } catch (error) {
      logger.error('Error during IndexedDB check:', error);
      setIsAvailable(false);
      setErrorReason('Failed to check IndexedDB availability');
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkAvailability();
  }, [retryCount]);

  const handleRetry = () => {
    logger.info('User requested IndexedDB check retry');
    setRetryCount(prev => prev + 1);
  };

  if (isChecking) {
    return <div className="indexeddb-guard-checking" />;
  }

  if (!isAvailable) {
    return (
      <div className="indexeddb-guard-container">
        <div className="indexeddb-guard-error-content">
          <div className="indexeddb-guard-error-icon">⚠️</div>
          <h1 className="indexeddb-guard-error-title">Storage Not Available</h1>
          <p className="indexeddb-guard-error-message">{errorReason}</p>

          {storageInfo && storageInfo.percentUsed > 90 && (
            <div className="indexeddb-guard-warning-box">
              <strong>Storage is nearly full!</strong>
              <p>Used: {(storageInfo.usage / 1024 / 1024).toFixed(2)}MB of {(storageInfo.quota / 1024 / 1024).toFixed(2)}MB</p>
            </div>
          )}

          <div className="indexeddb-guard-troubleshooting">
            <h3>How to fix this:</h3>
            <ul className="indexeddb-guard-list">
              <li>
                <strong>If using Private/Incognito mode:</strong>
                <br />Switch to a regular browser window. Private browsing restricts storage for security.
              </li>
              <li>
                <strong>If storage is full:</strong>
                <br />Clear browser data or free up space on your device.
              </li>
              <li>
                <strong>If using Safari:</strong>
                <br />Go to Settings → Safari → Advanced → Website Data and ensure storage is allowed.
              </li>
              <li>
                <strong>If blocked by settings:</strong>
                <br />Check your browser settings and ensure this site can use storage.
              </li>
            </ul>
          </div>

          <div className="indexeddb-guard-actions">
            <button onClick={handleRetry} className="indexeddb-guard-retry-button">
              Try Again
            </button>
            <a
              href="https://support.mozilla.org/en-US/kb/storage"
              target="_blank"
              rel="noopener noreferrer"
              className="indexeddb-guard-help-link"
            >
              Learn More
            </a>
          </div>

          {retryCount > 2 && (
            <div className="indexeddb-guard-persistent-error">
              <p>
                <strong>Still having issues?</strong>
                <br />
                Try using a different browser (Chrome, Firefox, or Edge recommended)
                or contact your system administrator.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return children;
};

IndexedDBGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default IndexedDBGuard;